// web-v2/src/lib/session.js
//
// SIGN-IN, SIGN-OUT AND WHO THIS IS (personal-logins L5, PLAN 8, DESIGN-REVIEW M11, m14). Relative
// imports only, so tests/sw-cache-clears.test.js and tests/token-kind-badge.test.js run it under node.
//
//   beginSession  clears the service worker's API cache, THEN stores the token: 'tracon-api' is keyed
//                 by URL, not by person (vite.config.js), so the next person on this browser must
//                 never be served the last one's answers.
//   endSession    clears the token and the API cache (sign-out and auth:expired).
//   fetchMe       GET /api/tracon/auth/me, once per held token; any failure is null (the badge hides)
//                 and is asked again next time. onIdentityChange tells useMe when another tab swaps it.
//   tokenBadge    the TopBar's words from /auth/me: the person, or "Shared login".
//   ownerPagesVisible  the owner, or the shared login while the bootstrap window is open. The server
//                 is the gate on every owner door; this only hides links.

import { getToken, setToken, clearToken } from './auth.js';
import { api } from './api.js';

export const API_CACHE = 'tracon-api';
export const ME_PATH = '/api/tracon/auth/me';
const TOKEN_KEY = 'tracon_token';

/** Deletes the API runtime cache; the precache (the app shell) stays. Never throws. */
export async function clearApiCaches({ cachesImpl = globalThis.caches } = {}) {
  if (!cachesImpl || typeof cachesImpl.keys !== 'function') return 0;
  try {
    const keys = await cachesImpl.keys();
    const mine = keys.filter((k) => k === API_CACHE);
    await Promise.all(mine.map((k) => cachesImpl.delete(k)));
    return mine.length;
  } catch (e) {
    console.error('[TRACON] the API cache could not be cleared', e && e.message);
    return 0;
  }
}

// The /auth/me answer belongs to ONE token (review L5-SEC-2): another tab, or V1 on the same origin,
// can swap tracon_token under this tab, and every api.js call then goes out as that other person.
// So the cache is keyed on the token held now, a 'storage' event for the key drops it and tells every
// mounted useMe, and the key's removal signs this tab out. A failed answer is never cached (L5-UX-8).
let mePromise = null;
let meToken;
const watchers = new Set();
export function resetMe() { mePromise = null; meToken = undefined; }
const heldToken = () => { try { return getToken(); } catch { return null; } };

/** Subscribes to "the stored token changed in another tab". Returns the unsubscribe. */
export function onIdentityChange(fn) { watchers.add(fn); return () => { watchers.delete(fn); }; }

/** The window 'storage' handler. true when it was about the token. */
export function tokenStorageChanged(e, { dispatch = (name) => globalThis.dispatchEvent(new Event(name)) } = {}) {
  if (!e || (e.key !== TOKEN_KEY && e.key !== null)) return false;
  resetMe();
  const now = heldToken();
  for (const fn of [...watchers]) { try { fn(now); } catch (err) { console.error('[TRACON] identity watcher threw', err && err.message); } }
  if (!now) { try { dispatch('auth:expired'); } catch { /* no window */ } }
  return true;
}
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') window.addEventListener('storage', (e) => tokenStorageChanged(e));

export async function beginSession(token, deps = {}) {
  await clearApiCaches(deps);
  setToken(token);
  resetMe();
}

export async function endSession(deps = {}) {
  clearToken();
  resetMe();
  try { globalThis.google?.accounts?.id?.disableAutoSelect?.(); } catch { /* GIS not loaded */ }
  await clearApiCaches(deps);
}

const validMe = (d) => !!d && d.success === true && (d.kind === 'personal' || d.kind === 'shared');

/** The server's answer to "who am I" for the token held now; null on any failure, and a failure is not kept. */
export function fetchMe() {
  const tok = heldToken();
  if (!mePromise || meToken !== tok) {
    meToken = tok;
    // 404 'Not found' is an API older than /auth/me (review L5-UX-4): no badge, and no toast on every load.
    const p = api.get(ME_PATH, { expected: [{ status: 503, code: 'identity_unavailable' }, { status: 404, code: 'Not found' }] })
      .then((d) => (validMe(d) ? d : null))
      .catch(() => null);
    mePromise = p;
    p.then((d) => { if (d === null && mePromise === p) resetMe(); });
  }
  return mePromise;
}

function initialsOf(s) {
  const parts = String(s || '').split('@')[0].split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0] || '').slice(0, 2).toUpperCase() || '?';
}

export function tokenBadge(me) {
  if (!me || typeof me !== 'object') return null;
  if (me.kind === 'personal' && me.user && typeof me.user === 'object') {
    const label = String(me.user.display_name || '').trim() || me.user.email || 'Signed in';
    return { kind: 'personal', label, title: `${me.user.email} (${me.user.role})`, initials: initialsOf(label) };
  }
  if (me.kind === 'shared') return { kind: 'shared', label: 'Shared login', title: 'Signed in with the shared TRACON password. Who you are is not recorded.', initials: 'SL' };
  return null;
}

export function ownerPagesVisible(me) {
  if (!me || typeof me !== 'object') return false;
  if (me.owner === true) return true;
  return me.kind === 'shared' && me.bootstrap === true;
}
